<template>
  <AsyncModalBody class="p-24" :is-error :is-loading :is-success>
    <template #content>
      <section class="pb-32">
        <h2 class="heading-2">
          {{ i18n(translations.section1Title) }}
        </h2>
        <div v-if="sellerDetails?.manufacturer">
          <div class="pt-16">
            <p class="body-1">
              {{ sellerDetails.manufacturer.companyName }}
            </p>
          </div>
          <div
            v-for="field in sellerDetails.manufacturer.fields"
            :key="field.label"
            class="pt-8"
          >
            <p class="caption-bold">
              {{ field.label }}
            </p>
            <p class="body-1">
              {{ field.value }}
            </p>
          </div>
        </div>
        <SellerDetailsModalNoInfo v-else class="mt-16" />
      </section>
      <RevDivider class="pb-32" />
      <section class="pb-32">
        <h2 class="heading-2">{{ i18n(translations.section2Title) }}</h2>
        <div v-if="sellerDetails?.responsibleOperator">
          <div class="pt-16">
            <p class="body-1">
              {{ sellerDetails.responsibleOperator.fields.businessName }}
            </p>
          </div>
          <div class="pt-8">
            <p class="caption-bold">
              {{ i18n(translations.section2Item2) }}
            </p>
          </div>
          <div class="pt-8">
            <p class="body-1">
              {{ sellerDetails.responsibleOperator.fields.email }}
            </p>
          </div>
          <div class="pt-8">
            <p class="body-1">
              {{ sellerDetails.responsibleOperator.fields.phone }}
            </p>
          </div>
          <div class="pt-8">
            <p class="body-1">
              {{ sellerDetails.responsibleOperator.fields.website }}
            </p>
          </div>
          <div class="pt-8">
            <p class="caption-bold">
              {{ i18n(translations.section2Item3) }}
            </p>
            <p class="body-1">
              {{ sellerDetails.responsibleOperator.fields.addressLine1 }}
            </p>
          </div>
        </div>
        <SellerDetailsModalNoInfo v-else class="mt-16" />
      </section>
      <RevDivider class="pb-32" />
      <section>
        <h2 class="heading-2">{{ i18n(translations.section3Title) }}</h2>
        <div class="pt-16">
          <div class="flex">
            <RevCountryFlag country-code="EU" />
            <span class="body-1-bold pl-8">
              {{ sellerDetails?.merchant?.fields.certification.label }}
            </span>
          </div>
          <div class="body-2">
            {{ sellerDetails?.merchant?.fields.certification.value }}
          </div>
        </div>
        <div v-if="lifespan" class="pt-20 pb-32">
          <div class="body-1-bold">
            {{ sellerDetails?.merchant?.fields.lifespan.label }}
          </div>
          <div class="body-1">
            {{
              i18n(translations.section3Item2Date, {
                years: lifespan.years,
                months: lifespan.months,
              })
            }}
          </div>
        </div>
        <RevDivider class="pb-32" />
        <section v-if="sellerDetails?.safetyInformation">
          <div class="body-1-bold">
            {{ sellerDetails.safetyInformation.title }}
          </div>
          <RevLink :to="sellerDetails.safetyInformation.url">
            <span class="body-2">
              {{ sellerDetails?.safetyInformation.text }}
            </span>
          </RevLink>
        </section>
        <RevButton
          v-if="sellerDetails?.merchant?.link"
          class="mt-20"
          full-width="always"
          size="medium"
          :to="sellerDetails.merchant.link"
          variant="secondary"
        >
          {{ i18n(translations.section3Button) }}
        </RevButton>
      </section>
    </template>
  </AsyncModalBody>
</template>

<script setup lang="ts">
import { useAsyncData, useRoute } from '#imports'
import { computed } from 'vue'

import AsyncModalBody from '@backmarket/nuxt-layer-product-modals/AsyncModalBody.vue'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevCountryFlag } from '@ds/components/CountryFlag'
import { RevDivider } from '@ds/components/Divider'
import { RevLink } from '@ds/components/Link'

import translations from '../../SellerDetailsModal.translations'
import { getMonthDifference } from '../../utils/getMonthDifference'
import SellerDetailsModalNoInfo from '../SellerDetailsModalNoInfo/SellerDetailsModalNoInfo.vue'

const props = defineProps<{
  listingId?: string | null
  productId?: string | null
}>()

const i18n = useI18n()
const route = useRoute()
const $httpFetch = useHttpFetch()

// [Apps]`listingId` is passed as a prop when rendered inside the drawer, but comes
// from the URL query when this body is rendered standalone as a webview route.
const listingId = computed(
  () => props.listingId ?? (route.query.listingId as string | undefined),
)

// [Apps]`productId` is passed as a prop when rendered inside the drawer, but comes
// from the URL query when this body is rendered standalone as a webview route.
const productId = computed(
  () => props.productId ?? (route.query.productId as string | undefined),
)

const { data: sellerDetails, status } = useAsyncData(
  async () => {
    if (!productId.value || !listingId.value) return undefined

    return await $httpFetch(
      '/product-page/products/:id/listings/:listingId/legal-details',
      {
        pathParams: {
          id: productId.value,
          listingId: String(listingId.value),
        },
      },
    )
  },
  { watch: [productId, listingId] },
)

const isError = computed(() => status.value === 'error')
const isSuccess = computed(() => status.value === 'success')
const isLoading = computed(
  () => status.value === 'pending' || status.value === 'idle',
)

const lifespan = computed(() => {
  if (sellerDetails.value?.merchant) {
    const sellerDate = new Date(
      sellerDetails.value?.merchant?.fields.lifespan.value,
    )
    const currentDate = new Date(Date.now())
    const differenceInMonths = getMonthDifference(sellerDate, currentDate)
    const years = Math.trunc(differenceInMonths / 12)
    const months = differenceInMonths - years * 12

    return { years, months }
  }

  return {}
})
</script>
